import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorIcon from "@mui/icons-material/Error";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Grid from "@mui/material/Grid";
import { format } from "date-fns";
import { Fragment } from "react";
import { Controller } from "react-hook-form";
import { toast } from "react-toastify";

import { UploadBox } from "@/atoms";
import {
    constantsData,
    distributionTypesData,
    driveManagementData,
    fileData,
    identifiersData,
    inputData,
    statusTypesData,
    tabsData,
    urlsData,
} from "@/data";
import { openAlert, toggleDistributionDrawer } from "@/redux/actions";
import { deleteData } from "@/services";

import { createDynamicField } from "./dynamic-fields";

const {
    audio: audioFileExtensions,
    csv: csvFileExtensions,
    document: documentFileExtensions,
    image: imageFileExtensions,
    lyrics: lyricsFileExtensions,
    motionArtwork: motionArtworkFileExtensions,
    sshKey: sshKeyFileExtensions,
    video: videoFileExtensions,
    videoThumbnail: videoThumbnailFileExtensions,
    xml: xmlFileExtensions,
    zip: zipFileExtensions,
} = fileData.extensions;

const {
    assetsXml: assetsXmlFileName,
    bulkUpdateRelease: bulkUpdateReleaseFileName,
    bulkUpdateTrack: bulkUpdateTrackFileName,
    document: documentFileName,
    image: imageFileName,
    lyrics: lyricsFileName,
    metadataXml: metadataXmlFileName,
    motionArtwork: motionArtworkFileName,
    releaseClientLinks: releaseClientLinksFileName,
    revenueReport: revenueReportFileName,
    revenueReportTemplate: revenueReportTemplateFileName,
    sshKey: sshKeyFileName,
    takedown: takedownFileName,
    track: trackFileName,
    video: videoFileName,
    videoThumbnail: videoThumbnailFileName,
    videoXml: videoXmlFileName,
    xml: xmlFileName,
    xmlType: xmlTypeFileName,
} = fileData.names;

export const updateStateHandler = (oldState, newState) => ({
    ...oldState,
    ...newState,
});

export const getJobStatusConfigHandler = (theme, t, data, entityType) => ({
    active: {
        icon: CloudUploadIcon,
        iconColor: theme.palette.primary.main,
        progressColor: theme.palette.success.third,
        subtitle: t("jobStatus.active.subtitle"),
        title: t("jobStatus.active.title"),
        variant: "indeterminate",
    },
    completed: {
        icon: CheckCircleIcon,
        iconColor: theme.palette.primary.main,
        progressColor: theme.palette.success.third,
        subtitle: t("jobStatus.completed.subtitle"),
        title: t("jobStatus.completed.title"),
    },
    delayed: {
        icon: ScheduleIcon,
        iconColor: theme.palette.grey[600],
        progressColor: theme.palette.progressBar.color.delayed,
        subtitle: t("jobStatus.delayed.subtitle"),
        title: t("jobStatus.delayed.title"),
    },
    failed: {
        icon: ErrorIcon,
        iconColor: theme.palette.error.main,
        progressColor: theme.palette.error.third,
        subtitle: data?.result?.message || t("jobStatus.failed.subtitle"),
        title: t("jobStatus.failed.title", { entity: entityType }),
    },
    paused: {
        icon: PauseCircleIcon,
        iconColor: theme.palette.warning.main,
        progressColor: theme.palette.warning.main,
        subtitle: t("jobStatus.paused.subtitle"),
        title: t("jobStatus.paused.title"),
    },
    waiting: {
        icon: ScheduleIcon,
        iconColor: theme.palette.grey[600],
        progressColor: theme.palette.progressBar.color.waiting,
        subtitle: t("jobStatus.waiting.subtitle"),
        title: t("jobStatus.waiting.title"),
    },
});

export const getProgressValueHandler = (statusType, progress) => {
    if (statusType === "completed") return 100;
    if (statusType === "failed" || statusType === "waiting" || statusType === "delayed") return 0;
    return progress !== undefined ? progress : 0;
};

/**
 * Resolves the type of a distribution job from the flags the job carries.
 *
 * TODO(RCMS-1451 / phase 3): `DistributeJob` does not expose `isAssetFilesUpdated` yet, so asset
 * updates fall back to `distributed`. Once the backend adds it, request it in
 * `getDistributionsListQuery` and this handler starts returning it without further changes.
 */
export const getDistributionTypeHandler = ({ isAssetFilesUpdated, isMetadataUpdated, isTakedown }) => {
    const { assetUpdate, distributed, metadataUpdate, takedown } = distributionTypesData;

    if (isTakedown) return takedown;

    if (isAssetFilesUpdated) return assetUpdate;

    if (isMetadataUpdated) return metadataUpdate;

    return distributed;
};

export const renderFormControllerHandler = (control, data, renderFormControllerChildrenHandler, hasDrawer) =>
    data.map(
        ({
            appleLabel,
            col,
            drawerCol,
            hasOffset,
            hasSwitch,
            isAsyncMenu,
            isOptional,
            label,
            name,
            selectionKey,
            switchLabel,
            type,
        }) => {
            const gridSize = hasDrawer ? (drawerCol ?? { xs: 12 }) : (col ?? { xs: 12 });

            return (
                <Fragment key={name}>
                    <Grid key={name} size={gridSize} item>
                        <Controller
                            control={control}
                            key={name}
                            name={name}
                            render={({ field }) =>
                                renderFormControllerChildrenHandler(
                                    field,
                                    label,
                                    name,
                                    type,
                                    isOptional,
                                    selectionKey,
                                    isAsyncMenu,
                                    hasSwitch,
                                    switchLabel,
                                    appleLabel,
                                )
                            }
                        />
                    </Grid>
                    {hasOffset && !hasDrawer && (
                        <Grid
                            size={{
                                xs: 12,
                            }}
                            sx={{
                                display: {
                                    md: "block",
                                    xs: "none",
                                },
                            }}
                            item
                        />
                    )}
                </Fragment>
            );
        },
    );

export const getSplicedArrayHandler = (array, setArray, value) => {
    const index = array.indexOf(value);

    if (index >= 0) {
        const clonedArray = [...array];

        clonedArray.splice(index, 1);

        setArray(clonedArray);

        return clonedArray;
    }
};

export const convertDateToLocaleStringFormatHandler = (date) => {
    if (Date.parse(date)) {
        const newDate = new Date(date);

        const year = newDate.getFullYear();

        let month = newDate.getMonth() + 1;

        let day = newDate.getDate();

        if (day < 10) day = `0${day}`;

        if (month < 10) month = `0${month}`;

        return `${day}/${month}/${year}`;
    }

    return "";
};

export const convertDateToLocaleDateTimeFormatHandler = (date) => {
    const localeDate = convertDateToLocaleStringFormatHandler(date);

    if (!localeDate) return "";

    const newDate = new Date(date);

    let hours = newDate.getHours();

    let minutes = newDate.getMinutes();

    let seconds = newDate.getSeconds();

    if (hours < 10) hours = `0${hours}`;

    if (minutes < 10) minutes = `0${minutes}`;

    if (seconds < 10) seconds = `0${seconds}`;

    return `${localeDate} ${hours}:${minutes}:${seconds}`;
};

export const convertDateToIsoFormatHandler = (date) => {
    let day = date.getDate().toString();

    let month = (date.getMonth() + 1).toString();

    const year = date.getFullYear().toString();

    if (day.length === 1) day = `0${day}`;

    if (month.length === 1) month = `0${month}`;

    const isoDate = [year, month, day].join("-");

    return isoDate;
};

export const convertTimeToSecondsFormatHandler = (time) => {
    const timeArray = time.split(":");

    const seconds = +timeArray[0] * 60 * 60 + +timeArray[1] * 60 + +timeArray[2];

    return seconds;
};

export const convertHourToDurationFormatHandler = (hour) => {
    let minutes = Math.floor(hour / 60);

    let seconds = hour % 60;

    minutes = minutes < 10 ? `0${minutes}` : minutes;

    seconds = seconds < 10 ? `0${Math.round(seconds)}` : Math.round(seconds);

    return `${minutes}:${seconds}`;
};

export const convertSecondsToFullDurationFormatHandler = (totalSeconds) => {
    let hours = Math.floor(totalSeconds / 3600);

    let minutes = Math.floor((totalSeconds % 3600) / 60);

    let seconds = Math.round(totalSeconds % 60);

    if (hours < 10) hours = `0${hours}`;

    if (minutes < 10) minutes = `0${minutes}`;

    if (seconds < 10) seconds = `0${seconds}`;

    return `${hours}:${minutes}:${seconds}`;
};

export const getNextDayDateHandler = (date) => {
    const newDate = new Date(date);

    newDate.setDate(date.getDate() + 1);

    if (newDate.getMonth() !== date.getMonth()) newDate.setDate(1);

    return new Date(newDate);
};

export const normalizeDateToUTCMidnightHandler = (dateValue) => {
    if (!dateValue) return dateValue;

    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);

    const localDateStr = format(date, "yyyy-MM-dd");

    return `${localDateStr}T00:00:00.000Z`;
};

export const reverseDateHandler = (date) => {
    if (!date) return;
    const splittedDate = date.includes("-") ? date?.split("-") : date?.split("/");

    const reversedDate = splittedDate?.reverse()?.join("-");

    return reversedDate;
};

export const checkFileValidationHandler = (file, name) => {
    switch (name) {
        case bulkUpdateReleaseFileName:
        case bulkUpdateTrackFileName:
            if (
                zipFileExtensions.find(
                    (extension) =>
                        file?.type?.toLowerCase()?.includes(extension) ||
                        file?.name?.toLowerCase()?.includes(extension),
                )
            )
                return true;
            break;
        case imageFileName:
            if (imageFileExtensions.find((extension) => file?.type.includes(extension))) return true;
            break;
        case videoThumbnailFileName:
            if (videoThumbnailFileExtensions.find((extension) => file?.type?.includes(extension))) return true;
            break;
        case xmlTypeFileName:
        case xmlFileName:
        case takedownFileName:
        case assetsXmlFileName:
        case metadataXmlFileName:
        case videoXmlFileName:
            if (xmlFileExtensions.find((extension) => file?.type.includes(extension))) return true;
            break;
        case sshKeyFileName:
            if (sshKeyFileExtensions.find((extension) => file?.name.includes(extension))) return true;
            break;
        case trackFileName:
            if (audioFileExtensions.find((extension) => file?.type.includes(extension))) return true;
            break;
        case videoFileName:
            if (
                videoFileExtensions.find(
                    (extension) => file?.type.includes(extension) || file?.name.includes(extension),
                )
            )
                return true;
            break;
        case revenueReportTemplateFileName:
        case revenueReportFileName:
            if (csvFileExtensions.find((extension) => file?.type.includes(extension))) return true;
            break;
        case lyricsFileName:
            if (!file?.type) if (lyricsFileExtensions.find((extension) => file?.name.includes(extension))) return true;

            if (lyricsFileExtensions.find((extension) => file?.type.includes(extension))) return true;
            break;
        case documentFileName:
        case releaseClientLinksFileName:
            if (!file?.type)
                if (documentFileExtensions.find((extension) => file?.name.includes(extension))) return true;

            if (documentFileExtensions.find((extension) => file?.type.includes(extension))) return true;
            break;
        case motionArtworkFileName:
            if (motionArtworkFileExtensions.find((extension) => file?.type.includes(extension))) return true;
            break;
        default:
            break;
    }
};

export const debounce = (callback, time) => {
    let timeout = null;

    return (value, ...rest) => {
        if (timeout) clearTimeout(timeout);

        timeout = setTimeout(() => callback(value, ...rest), time);
    };
};

export const getSubstringHandler = (string, length = 40, withoutEnd, endLength = -10) => {
    let subString = "";

    if (string?.length > length && !withoutEnd) {
        subString = `${string?.substr(0, length)}...${string.slice(endLength)}`;
    } else if (string?.length > length && withoutEnd) {
        subString = `${string?.substr(0, length)}...`;
    } else subString = string;

    return subString;
};

export const renderFilteredTabsHandler = (data, tabsDataKey, entities) => {
    let tabs = tabsData.data?.[tabsDataKey];

    entities.forEach(({ isObject, key, label }) => {
        if (isObject) {
            if (!(data?.[key] && Object?.keys(data?.[key]).length > 0))
                tabs = tabs.filter((tab) => tab.label !== label);
        } else if (!data?.[key]?.length > 0) tabs = tabs.filter((tab) => tab.label !== label);
    });

    return tabs;
};

export const getUniqueRandomId = () => `_${Math.random().toString(36).substr(2, 9)}`;

export const getFileSizeHandler = (bytes) => {
    switch (true) {
        case bytes < 1024:
            return `${bytes} bytes`;
        case bytes < 1048576:
            return `${(bytes / 1024).toFixed(2)} KB`;
        case bytes < 1073741824:
            return `${(bytes / 1048576).toFixed(2)} MB`;
        case bytes > 1073741824:
            return `${(bytes / 1073741824).toFixed(2)} GB`;
        default:
            return "_";
    }
};

export const updateUploadFileToastHandler = (file, progress, toastIdsRef, status, onCancelUpload) => {
    const toastId = toastIdsRef.current[file.name];

    const toastType = {
        error: toast.ERROR,
        inProgess: toast.INFO,
        success: toast.SUCCESS,
    };

    if (toastId) {
        if (toast.isActive(toastId)) {
            toast.update(toastId, {
                autoClose: status === statusTypesData.success ? 3000 : false,
                icon: false,
                render: <UploadBox file={file} progress={progress} status={status} onCancelUpload={onCancelUpload} />,
                type: toastType[status],
            });
        } else {
            const newToastId = toast.info(
                <UploadBox file={file} progress={progress} status={status} onCancelUpload={onCancelUpload} />,
                {
                    autoClose: status === statusTypesData.success ? 3000 : false,
                    closeButton: true,
                    icon: false,
                    toastId: file.name,
                    type: toastType[status],
                },
            );

            toastIdsRef.current[file.name] = newToastId;
        }
    } else {
        const newToastId = toast.info(
            <UploadBox file={file} progress={progress} status={status} onCancelUpload={onCancelUpload} />,
            {
                autoClose: status === statusTypesData.success ? 3000 : false,
                closeButton: true,
                icon: false,
                toastId: file.name,
                type: toastType[status],
            },
        );

        toastIdsRef.current[file.name] = newToastId;
    }
};

export const logoutHandler = async (logout, from, token) => {
    if (from) {
        localStorage.setItem("lastVisitedPage", from);
    }

    const logoutAction = () => {
        logout({
            logoutParams: { returnTo: window.location.origin },
        });
    };

    if (token) {
        await deleteData(urlsData.apis.driveManagement.logout, null, {
            logoutAction,
            token,
        });
    }

    logoutAction();
};

// bulkDistribute queues what it can and reports every release or video it could not as a failed result instead of
// throwing, so the drawer only closes when everything started. otherwise it stays open on the results to show why
export const displayBulkDistributionResultsHandler = (results, successMessage, setResults, dispatch, t) => {
    const failedCount = results?.filter(({ success }) => !success).length || 0;

    if (failedCount === 0) {
        dispatch(openAlert(successMessage, statusTypesData.success));

        dispatch(toggleDistributionDrawer(false));

        return;
    }

    const successCount = results.length - failedCount;

    dispatch(
        successCount > 0
            ? openAlert(
                  t("messages.distributionMsgs.bulk.partialSuccess", { failedCount, successCount }),
                  statusTypesData.info,
              )
            : openAlert(t("messages.distributionMsgs.bulk.failed"), statusTypesData.error),
    );

    setResults(results);
};

export const displayErrorHandler = (message, dispatch, action, status, logout) => {
    dispatch(action(message, statusTypesData.error));

    if (status === 401) {
        logoutHandler(logout, window.location.pathname);
    }
};

export const openLinkInNewTabHandler = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
};

export const copyTextHandler = (text, dispatch, message) => {
    navigator.clipboard.writeText(text).then(() => {
        dispatch(openAlert(message, statusTypesData.success));
    });
};

export const getDriveManagementUserPermissionsListHandler = (mimeType, isOrganizer) => {
    const finalList = driveManagementData.userPermissions.filter((_, index) => index !== 0 && index !== 1);

    if (!mimeType.includes(constantsData.folder) || !isOrganizer) return finalList.filter((_, index) => index !== 0);

    return finalList;
};

export const clearFormStateAfterSubmissionHandler = (
    response,
    filesData,
    setFilesData,
    setFormReset,
    getValues,
    setValue,
    clearErrors,
) => {
    if (!response?.error) {
        document.querySelectorAll(`.${identifiersData.classNames.clearIndicator}`).forEach((button) => button.click());

        Object.keys(getValues()).forEach((key) => {
            if (key === inputData.names.secondaryArtists) {
                setValue(key, [], { shouldDirty: false });
            } else {
                setValue(key, "", { shouldDirty: false });
            }
        });

        if (filesData) {
            filesData.forEach((file) => {
                setFilesData((prev) => ({
                    ...prev,
                    [file]: null,
                }));
            });
        }

        document.querySelectorAll("input[type='file']").forEach((input) => (input.value = ""));

        setTimeout(() => {
            clearErrors();
        }, [0]);

        if (setFormReset) setFormReset(true);
    }
};

export const deleteFileHandler = (setFilesData, formMethods, name) => {
    const fileInput = document.getElementById(`${inputData.names.file}_${identifiersData.ids.input}`);

    if (fileInput) fileInput.value = "";
    else document.getElementById(name + "_" + identifiersData.ids.input).value = "";

    setFilesData((prev) => ({
        ...prev,
        [name]: null,
    }));

    formMethods.setValue(name, null);
};

export const setUploadInputPreviousValueHandler = (watch, setError, setValue, filesData, fileName) => {
    if (!watch(fileName) && filesData[fileName]) {
        let value = "";

        switch (fileName) {
            case imageFileName:
                value = filesData[fileName]?.imageId;
                break;
            case sshKeyFileName:
            case documentFileName:
            case trackFileName:
            case videoFileName:
                value = filesData[fileName]?.id;
                break;
            default:
                value = filesData[fileName];
        }

        setValue(fileName, value);

        setTimeout(() => {
            setError(fileName, null);
        }, [10]);
    }
};

// `thumbnail` is the active one; videos created before it existed only carry the auto-generated `image`
export const getVideoThumbnailHandler = ({ image, imageId, thumbnail, thumbnailId } = {}) => ({
    activeThumbnail: thumbnail || image || null,
    isCustom: !!thumbnailId && thumbnailId !== imageId,
});

export const getVideoImageSrcHandler = (video) =>
    getVideoThumbnailHandler(video).activeThumbnail?.downloadUri ||
    video?.track?.image?.downloadThumbnail ||
    video?.release?.image?.downloadThumbnail;

export const getShortLabel = (label = "") => {
    const words = label.trim().split(" ");
    return words.length > 1 ? words[0] : label;
};

export const calculateTagsVisibilityHandler = ({ value, visibleChipCount }) => ({
    hiddenItems: value.slice(visibleChipCount),
    visibleItems: value.slice(0, visibleChipCount),
});

export const getDisabledOptions = (fields, currentIndex, keyToCheck) =>
    fields
        .map((f, i) => (i !== currentIndex && f[keyToCheck] ? f[keyToCheck] : null))
        .filter((value) => value !== null && value !== "");

export const convertFieldsToFormValues = (fields, fieldName, keyMappings) => {
    const validFields = fields
        .filter((field) =>
            keyMappings.every(({ key }) => {
                const value = field[key];
                if (typeof value === "number") return Number(value) > 0;
                if (Array.isArray(value)) return value.length > 0;
                return !!value;
            }),
        )
        .map((field) => {
            const result = {};
            keyMappings.forEach(({ key, transform }) => {
                let value = field[key];
                if (transform) value = transform(value);
                result[key] = value;
            });
            return result;
        });
    return { [fieldName]: validFields };
};

export const createInitialDynamicFields = (values, keyName, initialData = {}) => {
    const existingFields = values?.[keyName];
    if (existingFields && existingFields.length > 0) {
        return existingFields.map((item, index) => ({
            _cleared: false,
            _isNew: false,
            uniqueId: `${Date.now()}_${index}`,
            ...initialData,
            ...Object.keys(item).reduce((acc, key) => {
                acc[key] = item[key]?.value ?? item[key];
                return acc;
            }, {}),
        }));
    }
    return [createDynamicField(initialData)];
};
